// Stage Mail-V2 Composer (2026-07-23): MailRecipientsFields — shared To/Cc/Subject UI.
//
// 2026-07-24 重构:To/Cc 输入改用共享的 RecipientsChips 组件(chip + 多地址解析)。
//   - 单一数据源 = values.to/cc (string, draftHook 直通)
//   - 父组件(MailV2PageComposer / MailV2Detail)不需要任何改动
//
// Purpose:
//   共享的收件人字段组件。被 MailV2ComposeDialog / MailV2InlineDraftEditor /
//   MailV2InlineReplyEditor 三个外壳共同使用。
//
// 行为:
//   - To 必填,默认展开
//   - Cc 默认折叠,有数据时自动展开(与原 MailV2ComposeDialog 行为一致)
//   - 右侧 "Cc" 短链切换显隐
//   - 所有字段 disabled 由 caller 通过 values + onChange 控制
//
// Props:
//   values / onChange — controlled 字段
//   disabled — 整片禁用(sending 时)
//   showCc — 显隐状态由 caller 持有(共享后 caller 控制更灵活)

import React from "react";
import type { MailComposerFields } from "./mailComposerTypes";
import { RecipientsChips } from "./RecipientsChips";

export interface MailRecipientsFieldsProps {
  values: MailComposerFields;
  onChange: <K extends keyof MailComposerFields>(key: K, value: MailComposerFields[K]) => void;
  disabled?: boolean;
  showCc: boolean;
  onToggleCc: (next: boolean) => void;
}

export function MailRecipientsFields({
  values,
  onChange,
  disabled = false,
  showCc,
  onToggleCc,
}: MailRecipientsFieldsProps) {
  return (
    <div className="mv2-compose-fields">
      {/* To */}
      <div className="mv2-compose-field-row">
        <label className="mv2-compose-label">To</label>
        <RecipientsChips
          label="To"
          value={values.to}
          onChange={(v) => onChange("to", v)}
          placeholder="example@example.com"
          disabled={disabled}
          autoFocus
          ariaLabel="宛先 (To)"
          dataTestid="mv2-compose-to"
        />
        <div className="mv2-compose-ccbcc">
          {!showCc && (
            <button
              type="button"
              className="mv2-compose-ccbcc-btn"
              onClick={() => onToggleCc(true)}
              disabled={disabled}
            >
              Cc
            </button>
          )}
        </div>
      </div>

      {/* Cc(条件渲染) */}
      {showCc && (
        <div className="mv2-compose-field-row">
          <label className="mv2-compose-label">Cc</label>
          <RecipientsChips
            label="Cc"
            value={values.cc}
            onChange={(v) => onChange("cc", v)}
            placeholder="cc@example.com"
            disabled={disabled}
            ariaLabel="Cc"
            dataTestid="mv2-compose-cc"
          />
          <button
            type="button"
            className="mv2-compose-ccbcc-btn"
            onClick={() => onToggleCc(false)}
            disabled={disabled}
            title="Cc を閉じる"
            aria-label="Cc を閉じる"
          >
            ×
          </button>
        </div>
      )}

      {/* Subject */}
      <div className="mv2-compose-field-row">
        <label className="mv2-compose-label">件名</label>
        <input
          type="text"
          className="mv2-compose-input"
          placeholder="件名を入力"
          value={values.subject}
          onChange={(e) => onChange("subject", e.target.value)}
          disabled={disabled}
          data-testid="mv2-compose-subject"
        />
      </div>
    </div>
  );
}
